import { Injectable, Signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Wallet } from '../../../core/models';

import { ApiSettings } from '../../../core/config/api-settings';
import { WalletTransaction, WalletTransactionKind } from '../models/transaction.model';
import { UsageResult } from '../models/usage.model';

export interface WalletOverview {
  wallet: Wallet;
  recentTransactions: WalletTransaction[]; // 5 most recent, newest first
}

export interface CheckoutSession {
  url: string;
  sessionId: string;
  expiresAt: number; // Stripe's UNIX timestamp
  amountCents: number;
  currency: string;
}

export interface UsageQuery {
  from?: string;        // ISO date YYYY-MM-DD
  to?: string;
  endpoint?: string;
  apiKeyId?: string;
  source?: 'api' | 'playground';
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  constructor(private http: HttpClient) {
    this.wallet = toSignal(
      this.getWallet().pipe(shareReplay({ bufferSize: 1, refCount: false })),
      { initialValue: null },
    );
  }

  readonly wallet: Signal<WalletOverview | null>;

  getWallet(): Observable<WalletOverview> {
    const URL = `${ApiSettings.fullPath}wallet`;

    return this.http.get<any>(URL).pipe(
      map(res => ({
        wallet: new Wallet(res.wallet),
        recentTransactions: (res.recent_transactions ?? []).map((t: object) => new WalletTransaction(t)),
      })),
    );
  }

  getTransactions(args: { kind?: WalletTransactionKind; limit?: number } = {}): Observable<WalletTransaction[]> {
    const URL = `${ApiSettings.fullPath}wallet/transactions`;

    let params = new HttpParams();
    if (args.kind) params = params.set('kind', args.kind);
    if (args.limit) params = params.set('limit', args.limit);

    return this.http.get<any>(URL, { params }).pipe(
      map(res => (res.transactions ?? []).map((t: object) => new WalletTransaction(t))),
    );
  }

  getUsage(args: UsageQuery = {}): Observable<UsageResult> {
    const URL = `${ApiSettings.fullPath}usage`;

    let params = new HttpParams();
    if (args.from) params = params.set('from', args.from);
    if (args.to) params = params.set('to', args.to);
    if (args.endpoint) params = params.set('endpoint', args.endpoint);
    if (args.apiKeyId) params = params.set('api_key_id', args.apiKeyId);
    if (args.source) params = params.set('source', args.source);

    return this.http.get<any>(URL, { params }).pipe(
      map(res => new UsageResult(res)),
    );
  }

  createTopUp(amountCents: number): Observable<CheckoutSession> {
    const URL = `${ApiSettings.fullPath}wallet/topup`;

    return this.http.post<any>(URL, { amount_cents: amountCents }).pipe(
      map(res => ({
        url: res.checkout.url,
        sessionId: res.checkout.session_id,
        expiresAt: res.checkout.expires_at,
        amountCents: res.checkout.amount_cents,
        currency: res.checkout.currency,
      })),
    );
  }
}
